import { useTranslation } from "react-i18next";
import { useAudioContext } from "../../domains/app/contexts/audio.context";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { convertPodcastEpisodeToMediaData } from "../../domains/audio/utils/convertPodcastEpisodeToMediaData";
import type {
  ApiPodcastEpisode,
  ApiPodcastModel,
} from "../../domains/podcast/hooks/useGetPodcastsListQuery";
import { useSharePodcast } from "../../domains/podcast/hooks/useSharePodcast";
import { getPodcastBackground } from "../../domains/room/utils/getPodcastBackground";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import {
  gaEventSelectItemPodcastEpisode,
  gaEventViewItemPodcastEpisode,
} from "../../tools/analytics/domains/podcast.analytics";
import { ContentPublicationCard } from "../ui/ContentPublicationCard";
import { podcastSource } from "../ui/SourceLabel";

function formatDate(value: string | null, locale: string): string {
  if (value === null) return "";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Above this, the title alone fills the small row; below it, the row reads empty. */
const SHORT_TITLE_LENGTH = 60;

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}m ${remainder.toString().padStart(2, "0")}s`;
}

/**
 * One episode of a podcast show, in the Design System row shape shared by every
 * other content row (`PodcastCard`, `VideoCard`, `InfographicCard`). Tapping it
 * loads the show's published episodes into the player and starts on this one.
 */
export default function PodcastEpisodeCard({
  roomId,
  podcast,
  episode,
  listIndex,
  analyticsListName,
}: {
  roomId: string;
  podcast: ApiPodcastModel;
  episode: ApiPodcastEpisode;
  listIndex: number;
  analyticsListName: AnalyticsItemList;
}) {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const { triggerShare } = useSharePodcast();
  const {
    setTracks,
    setTrackTitle,
    setTrackImageUrl,
    setTrackCollectionName,
    setAudioPlayerPanelOpen,
  } = useAudioContext();

  const thumbnailUrl =
    episode.thumbnail === null
      ? getPodcastBackground(listIndex)
      : episode.thumbnail.url;
  const source = podcastSource(podcast.owner);
  // An episode carries no description, so a short title leaves the row shape
  // (title on the left, thumbnail on the right) mostly empty. Give those the
  // large shape instead, where the artwork leads and earns its place.
  const size = episode.title.length <= SHORT_TITLE_LENGTH ? "lg" : "sm";

  function play() {
    gaEventSelectItemPodcastEpisode({
      episode,
      owner: podcast.owner,
      listName: analyticsListName,
    });

    // The player queue is the whole show, so the reader keeps listening past
    // the episode they tapped. An episode with no audio file cannot be a track.
    const tracks = podcast.episodes
      .filter((ep) => ep.meta.status === "published")
      .flatMap((ep, index) => {
        if (ep.audio === null) return [];
        return [
          convertPodcastEpisodeToMediaData(
            ep,
            ep.audio.url,
            ep.thumbnail === null
              ? getPodcastBackground(index)
              : ep.thumbnail.url
          ),
        ];
      });

    const currentIndex = tracks.findIndex((track) => track.source === episode);
    if (currentIndex === -1) return;

    setTracks({
      track: { kind: "file" },
      contentList: tracks,
      index: currentIndex,
      autoplay: true,
    });
    setTrackTitle(episode.title);
    setTrackImageUrl(thumbnailUrl);
    setTrackCollectionName(podcast.title);
    setAudioPlayerPanelOpen(true);
    gaEventViewItemPodcastEpisode({
      episode,
      owner: podcast.owner,
      listName: analyticsListName,
    });
  }

  const common = {
    contentType: "podcast",
    title: episode.title,
    source,
    date: formatDate(episode.publication_date, activeLang),
    meta: t("Episode"),
    metaVariant: "lemon",
    detail: episode.duration === null ? null : formatDuration(episode.duration),
    recommendation: null,
    isBookmarked: false,
    onBookmark: null,
    onShare: () => {
      void triggerShare(podcast, roomId);
    },
    onClick: episode.audio === null ? null : play,
  } as const;

  return size === "lg" ? (
    <ContentPublicationCard {...common} size="lg" imageUrl={thumbnailUrl} />
  ) : (
    <ContentPublicationCard {...common} size="sm" thumbnailUrl={thumbnailUrl} />
  );
}
